import {
  IconCalendarEvent,
  IconChartLine,
  IconCalendarUser,
  IconDoorEnter,
  IconHeartHandshake,
  IconSend,
  IconShoppingBag,
  IconHome,
  IconIdBadge,
  IconLifebuoy,
  IconMapPin,
  IconSettings,
  IconUsers,
  IconUsersGroup,
  IconUserShield,
  IconWorldWww,
} from "@tabler/icons-react";
import type { PermissionQuery } from "@how/app-kit";
import type { ModuleKey, PermissionKey } from "@how/types";
import type { AppFrameNavItem } from "@how/ui";

export interface GatedNavItem extends AppFrameNavItem {
  /** Shown only when authorize_check allows it; navigation is convenience, the server still decides. */
  readonly permission?: PermissionKey;
  /** Shown when authorize_check allows any one of these: for a screen two permissions open (the assigned scope). */
  readonly anyOf?: readonly PermissionKey[];
  /** Shown only when the module is usable in this church. */
  readonly module?: ModuleKey;
  /** The entry carries a count (the inbox's unread conversations) for those holding this permission. */
  readonly countPermission?: PermissionKey;
}

/** Kept free of side effects so tests can import it. */
export const NAV: readonly GatedNavItem[] = [
  { to: "/", label: "Home", icon: IconHome },
  {
    to: "/people",
    label: "People",
    icon: IconUsers,
    permission: "people.read",
    module: "core_crm",
  },
  // Care: the queue of prayer and help requests (D13); staff read it, leaders and coordinators look after it.
  {
    to: "/care",
    label: "Care",
    icon: IconHeartHandshake,
    permission: "care.read",
    module: "core_crm",
  },
  // Membership: the coordinator's screen; owners and administrators hold it, the staff role does not.
  {
    to: "/membership",
    label: "Membership",
    icon: IconIdBadge,
    permission: "membership.read",
    module: "membership",
  },
  // Communications: audiences now, campaigns and templates as Milestone 4 track A lands (ADR 0035). The permissions
  // are cross-module by design; the module gate arrives with the provider adapter.
  {
    to: "/communications",
    label: "Communications",
    icon: IconSend,
    permission: "communications.read",
    // The texting inbox's unread conversations (#314), for those who read the inbox.
    countPermission: "communications.inbox",
  },
  {
    to: "/website",
    label: "Website",
    icon: IconWorldWww,
    permission: "website.read",
    module: "website",
  },
  // Staff with groups.read see every group; a leader with groups.read_assigned sees the ones they lead (ADR 0040).
  {
    to: "/groups",
    label: "Groups",
    icon: IconUsersGroup,
    anyOf: ["groups.read", "groups.read_assigned"],
    module: "groups",
  },
  {
    to: "/events",
    label: "Events",
    icon: IconCalendarEvent,
    permission: "events.read",
    module: "events",
  },
  // The door: events.check_in is granted on purpose (ADR 0042); owners and administrators hold it, staff do not.
  {
    to: "/check-in",
    label: "Check-in",
    icon: IconDoorEnter,
    permission: "events.check_in",
    module: "event_checkin",
  },
  // The rota: services.read is in the staff role; the grid changes only with services.manage (ADR 0043, ADR 0069).
  {
    to: "/services",
    label: "Services",
    icon: IconCalendarUser,
    permission: "services.read",
    module: "services",
  },
  // Store (ADR 0047): orders to hand over and the products members can buy in the portal.
  {
    to: "/store",
    label: "Store",
    icon: IconShoppingBag,
    permission: "commerce.read",
    module: "commerce",
  },
  // Reports (ADR 0052): the four reports; owners and administrators hold reports.advanced, with the add-on.
  {
    to: "/reports",
    label: "Reports",
    icon: IconChartLine,
    permission: "reports.advanced",
    module: "advanced_reporting",
  },
  // Owners and administrators, not owners alone: a campus is a record every church has (ADR 0036), and
  // campuses.manage is a base-module permission, unlike settings.manage.
  { to: "/campuses", label: "Campuses", icon: IconMapPin, permission: "campuses.manage" },
  // Team (ADR 0054, ADR 0036 amendment): who has a login, their roles and campuses, and the church's own roles.
  // users.read is an owner's and an administrator's; changing anything there asks for users.manage.
  { to: "/team", label: "Team", icon: IconUserShield, permission: "users.read" },
  { to: "/support", label: "Support access", icon: IconLifebuoy, permission: "settings.manage" },
  { to: "/settings", label: "Settings", icon: IconSettings, permission: "settings.manage" },
];

/**
 * The permission checks behind the navigation, without each entry's module: an entry is shown only when its module is
 * usable anyway, and a check without one is the same request a screen's own RequirePermission makes, so the two share
 * it rather than the screen waiting on another round trip (#286). Started with the rest at start-up.
 */
export const NAV_CHECKS: readonly PermissionQuery[] = NAV.flatMap((item) =>
  [
    ...(item.permission === undefined ? [] : [item.permission]),
    ...(item.anyOf ?? []),
    ...(item.countPermission === undefined ? [] : [item.countPermission]),
  ].map((permission) => ({ permission })),
);
